import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Compass,
  Folders,
  GearSix,
  House,
  Wallet as WalletIcon,
} from "@phosphor-icons/react";

import { defaultIconProps, type IconComponent } from "../features/shared/ui/icons";
import { useMobileNavVisibility } from "../hooks/useMobileNavVisibility";

type MobileNavItem = {
  id: string;
  label: string;
  to: string;
  icon: IconComponent;
};

export function MobileNavBar({ visible }: { visible?: boolean }) {
  const { t } = useTranslation();
  const { shouldShowMobileNav } = useMobileNavVisibility();

  const isVisible = visible ?? shouldShowMobileNav;
  if (!isVisible) {
    return null;
  }

  const navItems: MobileNavItem[] = [
    {
      id: "dashboard",
      label: t("navigation.mobile.dashboard"),
      to: "/dashboard",
      icon: House,
    },
    {
      id: "workspaces",
      label: t("navigation.mobile.properties"),
      to: "/workspaces",
      icon: Folders,
    },
    {
      id: "marketplace",
      label: t("navigation.mobile.marketplace"),
      to: "/workspaces/map",
      icon: Compass,
    },
    {
      id: "wallet",
      label: t("navigation.mobile.wallet"),
      to: "/billing",
      icon: WalletIcon,
    },
    {
      id: "settings",
      label: t("navigation.mobile.settings"),
      to: "/settings/profile",
      icon: GearSix,
    },
  ];

  return (
    <nav
      aria-label={t("navigation.mobile.aria_label")}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl px-3 pb-safe">
        <div className="mb-3 grid grid-cols-5 items-center gap-1 rounded-3xl border border-slate-200 bg-white/95 px-2 py-2 shadow-[0_24px_70px_-24px_rgba(0,0,0,0.45)] backdrop-blur supports-[backdrop-filter]:bg-white/90 dark:border-slate-800 dark:bg-slate-900/90">
          {navItems.map((item) => {
            const Icon = item.icon;
            const baseClasses =
              "flex h-full min-h-[64px] flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-semibold leading-tight transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 text-slate-600 hover:bg-primary-50 hover:text-primary-700 dark:text-slate-300 dark:hover:bg-primary-900/40 dark:hover:text-primary-50";

            return (
              <NavLink
                key={item.id}
                to={item.to}
                className={baseClasses}
                aria-label={item.label}
              >
                <Icon {...defaultIconProps} aria-hidden />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
